import React, { useState, useEffect } from 'react';
import { ArrowLeft, Phone, Building2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { circlesDivisionsService, CircleDivision } from '../../services/api/circlesDivisionsService';

const CirclesDivisionsView: React.FC = () => {
  const navigate = useNavigate();
  const [officers, setOfficers] = useState<CircleDivision[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalCircles: 0,
    totalSuperintendingEngineers: 0,
    totalExecutiveEngineers: 0,
    totalFACOfficers: 0,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [officersResponse, statsResponse] = await Promise.all([
        circlesDivisionsService.getAll(),
        circlesDivisionsService.getStatistics(),
      ]);

      setOfficers(officersResponse.data || []);
      setStats(statsResponse.data);
    } catch (error) {
      toast.error('Failed to fetch circles & divisions data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="inline-block w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-gray-600">Loading circles & divisions...</p>
        </div>
      </div>
    );
  }

  // Filter only Superintending Engineers (parent records with children)
  const superintendingEngineers = officers.filter(
    (officer) => officer.level_type === 'superintending_engineer'
  );

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Circles & Divisions</h1>
          <p className="mt-1 text-sm text-gray-600">
            PRED organizational structure - Circles and Divisions
          </p>
        </div>
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
        >
          <ArrowLeft size={18} className="mr-2" />
          Back
        </button>
      </div>

      {/* Main Content */}
      <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <tbody className="bg-white">
              {superintendingEngineers.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-8 text-center text-gray-500">
                    No circles & divisions data available
                  </td>
                </tr>
              ) : (
                superintendingEngineers.map((se) => {
                  const executiveEngineers = se.children || [];
                  const totalRows = 1 + executiveEngineers.length;

                  return (
                    <React.Fragment key={se.id}>
                      {/* Superintending Engineer Row */}
                      <tr className="border-b border-gray-200">
                        <td
                          rowSpan={totalRows}
                          className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900 border-r border-gray-200 align-top"
                        >
                          {se.circle_number}
                        </td>
                        <td
                          rowSpan={totalRows}
                          className="px-6 py-4 text-sm font-bold text-gray-900 border-r border-gray-200 align-top"
                        >
                          {se.sub_number}
                        </td>
                        <td className="px-6 py-4 border-r border-gray-200">
                          <div className="font-bold text-gray-900">{se.name}</div>
                          <div className="text-sm text-gray-600">{se.designation}</div>
                          <div className="text-sm text-gray-600 mt-1">{se.location}</div>
                          {se.is_fac && (
                            <span className="inline-block mt-1 px-2 py-0.5 text-xs font-semibold text-purple-800 bg-purple-100 rounded">
                              FAC
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center text-sm text-gray-900">
                            <Phone size={14} className="mr-2 text-gray-400 flex-shrink-0" />
                            {se.contact_number}
                          </div>
                        </td>
                      </tr>

                      {/* Executive Engineers Rows */}
                      {executiveEngineers.map((ee) => (
                        <tr key={ee.id} className="border-b border-gray-200">
                          <td className="px-6 py-4 border-r border-gray-200">
                            <div className="text-gray-900">{ee.name}</div>
                            <div className="text-sm text-gray-600">{ee.designation}</div>
                            <div className="text-sm text-gray-600 mt-1">{ee.location}</div>
                            {ee.is_fac && (
                              <span className="inline-block mt-1 px-2 py-0.5 text-xs font-semibold text-purple-800 bg-purple-100 rounded">
                                FAC
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center text-sm text-gray-900">
                              <Phone size={14} className="mr-2 text-gray-400 flex-shrink-0" />
                              {ee.contact_number}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Circles</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{stats.totalCircles}</p>
            </div>
            <div className="p-3 rounded-lg bg-blue-100">
              <Building2 size={24} className="text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Superintending Engineers</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">
                {stats.totalSuperintendingEngineers}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-green-100">
              <Building2 size={24} className="text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Executive Engineers</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">
                {stats.totalExecutiveEngineers}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-purple-100">
              <Building2 size={24} className="text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">FAC Officers</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{stats.totalFACOfficers}</p>
            </div>
            <div className="p-3 rounded-lg bg-yellow-100">
              <Building2 size={24} className="text-yellow-600" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CirclesDivisionsView;